import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationsStoreService } from './notifications-store.service';
import { NotificationNavigationService } from './notification-navigation.service';
import {
  AppNotification,
  NotificationCta,
  NotificationFilter,
  NotificationTarget,
  NotificationType,
} from './notification.model';

const RELATIVE_FMT = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const ABS_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

@Component({
  selector: 'bhub-notifications',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayPanelModule, TagModule, TooltipModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent {
  readonly store = inject(NotificationsStoreService);
  private readonly _nav = inject(NotificationNavigationService);

  @ViewChild('panel') panel!: OverlayPanel;

  readonly filters: { id: NotificationFilter; label: string }[] = [
    { id: 'all',           label: 'All' },
    { id: 'unread',        label: 'Unread' },
    { id: 'actionRequired', label: 'Action Required' },
    { id: 'warnings',      label: 'Warnings' },
  ];

  togglePanel(event: Event): void {
    this.panel.toggle(event);
  }

  setFilter(filter: NotificationFilter): void {
    this.store.setFilter(filter);
  }

  onRowClick(notif: AppNotification): void {
    this.store.markRead(notif.id);
    if (notif.target) {
      this._nav.navigateTo(notif.target);
      this.panel.hide();
    }
  }

  onCta(event: Event, cta: NotificationCta): void {
    event.stopPropagation();
    this._nav.navigateTo(cta.target);
    this.panel.hide();
  }

  onDismiss(event: Event, id: string): void {
    event.stopPropagation();
    this.store.dismiss(id);
  }

  onMarkAllRead(): void {
    this.store.markAllRead();
  }

  typeIcon(type: NotificationType): string {
    return this.store.typeIcon(type);
  }

  typeSeverity(type: NotificationType): string {
    const map: Record<NotificationType, string> = {
      info:           'info',
      warning:        'warn',
      error:          'danger',
      success:        'success',
      actionRequired: 'danger',
    };
    return map[type];
  }

  relativeTime(iso: string): string {
    const diffMs = new Date(iso).getTime() - Date.now();
    const diffMin = Math.round(diffMs / 60000);
    if (Math.abs(diffMin) < 60) return RELATIVE_FMT.format(diffMin, 'minute');
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return RELATIVE_FMT.format(diffHr, 'hour');
    const diffDay = Math.round(diffHr / 24);
    return RELATIVE_FMT.format(diffDay, 'day');
  }

  absoluteTime(iso: string): string {
    return ABS_FMT.format(new Date(iso));
  }

  trackNotif(_: number, n: AppNotification): string { return n.id; }
  trackFilter(_: number, f: { id: NotificationFilter; label: string }): string { return f.id; }

  hasTarget(notif: AppNotification): boolean { return !!notif.target; }
  hasCta(notif: AppNotification): notif is AppNotification & { cta: NotificationCta } {
    return !!notif.cta;
  }
}
